import { gql } from "@apollo/client";
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Select,
  Text,
} from "@chakra-ui/react";
import { Field, useField } from "formik";
import { InputControl } from "formik-chakra-ui";
import _ from "lodash";
import React, { useCallback, useMemo } from "react";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import { TextareaWithValidationControl } from "src/components/Inputs/TextareaWithValidationControl";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { isNonEmptyString } from "src/services/predicates";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { z } from "zod";

export const Form = () => {
  const [descriptionField, descriptionFieldValidation, descriptionHelpers] =
    useField("alerts.description");
  const [showTypeField, showTypeMeta] = useField("alerts.showType");

  const descriptionCounter = useMemo(
    () => DESCRIPTION_CONTENT_LIMIT - (descriptionField.value?.length ?? 0),
    [descriptionField.value]
  );

  const handleOnChangeDescription = useCallback(() => {
    if (descriptionFieldValidation.touched) return;

    descriptionHelpers.setTouched(true);
  }, [descriptionHelpers, descriptionFieldValidation.touched]);

  let showTypeHelperText: React.ReactNode = null;
  switch (showTypeField.value) {
    case GQL.announcement_display_type_enum.BANNER:
      showTypeHelperText = (
        <FormHelperText>
          The alert will appear on the family portal homepage. It will not
          prevent the parent from completing any actions.
        </FormHelperText>
      );
      break;
    case GQL.announcement_display_type_enum.DIALOG:
      showTypeHelperText = (
        <FormHelperText>
          The alert will appear on the family portal homepage. The parent must
          acknowledge the message to close the dialog and take any actions.
        </FormHelperText>
      );
  }

  return (
    <Flex direction="column" gap={2}>
      <Heading as="h3" variant="admin">
        Alerts
      </Heading>
      <FormikSwitchableContent<"enabled" | "disabled">
        label="Alert when no students belong to a parent"
        description={`An alert that’s displayed to parents when they have no students.
This alert will always display to the parent once every time they
login as long as they have no students.`}
        showContent={(status) => status === "enabled"}
        name="alerts.status"
        checked={["enabled", ""]}
        unchecked={["disabled", ""]}
      >
        <Flex direction="column" gap="4">
          <FormControl isInvalid={showTypeMeta.error !== undefined}>
            <FormLabel htmlFor="alerts.showType">Type</FormLabel>
            <Field as={Select} id="alerts.showType" name="alerts.showType">
              {(
                Object.keys(
                  ANNOUNCEMENT_DISPLAY_TYPES
                ) as GQL.announcement_display_type_enum[]
              ).map((key) => (
                <option
                  value={ANNOUNCEMENT_DISPLAY_TYPES[key].value}
                  key={ANNOUNCEMENT_DISPLAY_TYPES[key].value}
                >
                  {ANNOUNCEMENT_DISPLAY_TYPES[key].label}
                </option>
              ))}
            </Field>
            {showTypeHelperText}
            <FormErrorMessage>{showTypeMeta.error}</FormErrorMessage>
          </FormControl>

          <InputControl
            label="Title"
            inputProps={{ placeholder: "Enter a title" }}
            name="alerts.title"
            isRequired
          />

          <FormControl>
            <FormLabel htmlFor="alerts.linkActive">Link</FormLabel>
            <FormikSwitchableContent<"enabled" | "disabled">
              label="Show link"
              description="Show a button in the dialog that links to an external site."
              name="alerts.link.status"
              checked={["enabled", ""]}
              unchecked={["disabled", ""]}
              showContent={(status) => status === "enabled"}
            >
              <Flex direction="column">
                <InputControl
                  label="Link text"
                  inputProps={{ placeholder: "e.g.: Action button text" }}
                  name="alerts.link.text"
                  isRequired
                />

                <InputControl
                  label="Link URL"
                  inputProps={{ placeholder: "e.g.: https://avela.org/" }}
                  name="alerts.link.url"
                  isRequired
                />
              </Flex>
            </FormikSwitchableContent>
          </FormControl>
          <Flex direction="column" gap="2">
            <TextareaWithValidationControl
              label="Description"
              textareaProps={{ placeholder: "Enter a description" }}
              name="alerts.description"
              isRequired
              onChange={handleOnChangeDescription}
            />

            {(!descriptionFieldValidation.touched ||
              !descriptionFieldValidation.error) && (
              <Text fontSize="sm" lineHeight="1rem">
                {descriptionCounter}
              </Text>
            )}
          </Flex>
        </Flex>
      </FormikSwitchableContent>
    </Flex>
  );
};

export function useService(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const organization = useOrganization();
  const { remoteData, refetch } = useRemoteDataQuery<
    GQL.GetNoStudentAnnouncementByOrganization,
    GQL.GetNoStudentAnnouncementByOrganizationVariables
  >(
    gql`
      query GetNoStudentAnnouncementByOrganization($organizationId: uuid!) {
        announcement(
          where: {
            organization_id: { _eq: $organizationId }
            condition: { _eq: NO_STUDENT }
          }
        ) {
          id
          title
          active
          description
          condition
          display_type
          entry_point
          link_url
          link_text
          type
        }
      }
    `,
    {
      variables: { organizationId: organization.toNullable()?.id ?? "" },
      skip: !organization.hasData(),
    }
  );

  const initialValues = useMemo(() => {
    return remoteData
      .mapError<Error>(_.identity)
      .andThen((data): RD.RemoteData<Error, FormikType> => {
        const announcement = data.announcement[0];
        if (data.announcement.length > 1) {
          const error =
            "Found more than 1 NO_STUDENT announcement. We currently only support 1 announcement.";
          console.error(error);
          return RD.failure(new Error(error));
        }

        if (!announcement) {
          return RD.success({
            alerts: {
              id: undefined,
              status: "disabled",
              showType: GQL.announcement_display_type_enum.BANNER,
              title: "",
              description: "",
              link: {
                status: "disabled",
                text: "",
                url: "",
              },
            },
          });
        }
        let link: LinkType;
        if (
          isNonEmptyString(announcement.link_text) &&
          isNonEmptyString(announcement.link_url)
        ) {
          link = {
            status: "enabled",
            text: announcement.link_text,
            url: announcement.link_url,
          };
        } else {
          link = {
            status: "disabled",
            text: "",
            url: "",
          };
        }

        return RD.success<Error, FormikType>({
          alerts: {
            status: announcement.active ? "enabled" : "disabled",
            id: announcement.id,
            title: announcement.title,
            description: announcement.description,
            showType: announcement.display_type,
            link,
          },
        });
      });
  }, [remoteData]);

  const [upsertAnnouncement] = useRemoteDataMutation<
    GQL.UpsertAnnouncement,
    GQL.UpsertAnnouncementVariables
  >(gql`
    mutation UpsertAnnouncement($announcement: announcement_insert_input!) {
      insert_announcement_one(
        object: $announcement
        on_conflict: {
          constraint: announcement_pkey
          update_columns: [
            title
            description
            active
            type
            entry_point
            condition
            display_type
            link_text
            link_url
          ]
        }
      ) {
        id
      }
    }
  `);

  const [disableAnnouncement] = useRemoteDataMutation(gql`
    mutation DisableAnnouncement($id: uuid!) {
      update_announcement_by_pk(
        pk_columns: { id: $id }
        _set: { active: false }
      ) {
        id
      }
    }
  `);

  const submit = useCallback(
    async (values: FormikType) => {
      if (!organization.hasData()) {
        throw new Error("Missing organization");
      }

      if (_.isEqual(values.alerts, initialValues.toNullable()?.alerts)) {
        // no changes, skip update
        return;
      }

      const organizationId = organization.data.id;

      if (values.alerts.status === "disabled") {
        const id = values.alerts.id;
        if (id !== undefined) {
          await disableAnnouncement({
            variables: {
              id,
            },
          });
        }
      } else {
        await upsertAnnouncement({
          variables: {
            announcement: {
              id: values.alerts.id ? values.alerts.id : undefined,
              organization_id: organizationId,
              title: values.alerts.title.trimEnd(),
              description: values.alerts.description.trimEnd(),
              active: true,
              type: GQL.announcement_type_enum.INFO,
              entry_point: GQL.announcement_entry_point_enum.PARENT_PORTAL,
              condition: GQL.announcement_condition_enum.NO_STUDENT,
              display_type: values.alerts.showType,
              link_text:
                values.alerts.link.status === "enabled"
                  ? values.alerts.link.text
                  : null,
              link_url:
                values.alerts.link.status === "enabled"
                  ? values.alerts.link.url
                  : null,
            },
          },
        });
      }

      await refetch();
    },
    [
      disableAnnouncement,
      initialValues,
      organization,
      refetch,
      upsertAnnouncement,
    ]
  );

  return {
    initialValues,
    submit,
  };
}

interface AnnouncementDisplayType {
  label: string;
  value: GQL.announcement_display_type_enum;
}

type AnnouncementDisplayTypes = Record<
  GQL.announcement_display_type_enum,
  AnnouncementDisplayType
>;

const ANNOUNCEMENT_DISPLAY_TYPES: AnnouncementDisplayTypes = {
  [GQL.announcement_display_type_enum.BANNER]: {
    label: "Banner",
    value: GQL.announcement_display_type_enum.BANNER,
  },
  [GQL.announcement_display_type_enum.DIALOG]: {
    label: "Dialog",
    value: GQL.announcement_display_type_enum.DIALOG,
  },
};

export const DESCRIPTION_CONTENT_LIMIT = 550;

const DisabledLinkSchema = z.object({
  status: z.literal("disabled"),
  text: z.string(),
  url: z.string(),
});
const LinkSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("enabled"),
    text: z.string().min(1, "This field is required"),
    url: z.string().min(1, "This field is required"),
  }),
  DisabledLinkSchema,
]);

const AlertsSchema = z.object({
  status: z.literal("enabled"),
  id: z.string().optional(),
  title: z.string().min(1, { message: "This field is required" }),
  description: z
    .string()
    .max(DESCRIPTION_CONTENT_LIMIT, {
      message: `Description must be ${DESCRIPTION_CONTENT_LIMIT} characters or less.`,
    })
    .min(1, { message: "This field is required" }),
  showType: z.nativeEnum(GQL.announcement_display_type_enum, {
    message: "This field is required",
  }),
  link: LinkSchema,
});

const DisabledSchema = z.object({
  status: z.literal("disabled"),
  id: z.string().optional(),
  title: z.string(),
  description: z.string(),
  showType: z.nativeEnum(GQL.announcement_display_type_enum),
  link: LinkSchema,
});

export const FormikSchema = z.object({
  alerts: z.discriminatedUnion("status", [DisabledSchema, AlertsSchema]),
});

type LinkType = z.infer<typeof LinkSchema>;
type FormikType = z.infer<typeof FormikSchema>;
